import { useEffect, useRef, useState } from "react";
import { Search, User, Calendar, BedDouble, Menu, X } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Topbar.css";
import NotificationBell from "./NotificationBell";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

type Result = {
  type: "guest" | "booking" | "room";
  label: string;
  sub: string;
  path: string;
};

interface TopbarProps {
  onOpenSidebar?: () => void;
}

export default function Topbar({ onOpenSidebar }: TopbarProps) {
  const token = localStorage.getItem("ai2_token");
  const navigate = useNavigate();

  const [managerName, setManagerName] = useState("");
  const [hotelName, setHotelName]     = useState("");
  const [hotelLogo, setHotelLogo]     = useState("");
  const [timezone, setTimezone]       = useState("");
  const [clock, setClock]             = useState("");
  const [hotelClusterName, setHotelClusterName] = useState("");

  // search
  const [query, setQuery]             = useState("");
  const [results, setResults]         = useState<Result[]>([]);
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [cache, setCache]             = useState<{ guests: any[]; bookings: any[]; rooms: any[] } | null>(null);
  const inputRef       = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const boxRef         = useRef<HTMLDivElement>(null);

  // ── JWT decode ───────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    try {
      const decoded: any = jwtDecode(token);
      setManagerName(decoded.name);
      setHotelClusterName(decoded.hotelId || decoded.hotelClusterName);
    } catch {}
  }, [token]);

  // ── Hotel settings ───────────────────────────────────────────────
  useEffect(() => {
    if (!hotelClusterName) return;
    axios.get(`${API}/api/hotel/settings/${hotelClusterName}`)
      .then(res => {
        setHotelName(res.data.hotelName || res.data.hotel_name || res.data.name || "");
        setHotelLogo(res.data.logoURL || res.data.logo_url || res.data.logourl || res.data.logo || "");
        setTimezone(res.data.timezone || res.data.time_zone || "Africa/Cairo");
      })
      .catch(() => {});
  }, [hotelClusterName]);

  // ── Clock ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!timezone) return;
    const tick = () => setClock(new Date().toLocaleString("en-US", {
      timeZone: timezone, hour: "2-digit", minute: "2-digit", hour12: false,
    }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [timezone]);

  // ── Click outside to close ───────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetch all data once (cached) ─────────────────────────────────
  const fetchAll = async () => {
    if (cache) return cache;
    setLoading(true);
    try {
      const [gRes, bRes, rRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/guests`),
        axios.get(`${API}/api/bookings`),
        axios.get(`${API}/api/rooms`),
      ]);
      const fresh = {
        guests:   Array.isArray(gRes.data) ? gRes.data : [],
        bookings: Array.isArray(bRes.data) ? bRes.data : [],
        rooms:    Array.isArray(rRes.data) ? rRes.data : [],
      };
      setCache(fresh);
      return fresh;
    } catch {
      return { guests: [], bookings: [], rooms: [] };
    } finally {
      setLoading(false);
    }
  };

  // ── Search logic ─────────────────────────────────────────────────
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) { setResults([]); setOpen(false); return; }

    const role = (() => {
      try { return (jwtDecode(token || "") as any).role || "manager"; } catch { return "manager"; }
    })();
    const guestBase = role === "reception" ? "/dashboard/reception" : "/dashboard/manager";

    (async () => {
      const data = await fetchAll();
      const out: Result[] = [];

      // guests
      data.guests.forEach((g: any) => {
        const name  = (g.name || "").toLowerCase();
        const phone = (g.phoneNumber || g.phonenumber || "").toLowerCase();
        const email = (g.email || "").toLowerCase();
        if (name.includes(q) || phone.includes(q) || email.includes(q)) {
          const ph = (g.phoneNumber || g.phonenumber || "").replace(/\D/g, "");
          out.push({
            type: "guest",
            label: g.name || "Unknown",
            sub: g.phoneNumber || g.phonenumber || "",
            path: `${guestBase}/guests/${ph}`,
          });
        }
      });

      // bookings
      data.bookings.forEach((b: any) => {
        const name = (b.guestname || "").toLowerCase();
        const id   = (b.bookingid || "").toLowerCase();
        const room = String(b.roomid || b.roomnumber || "").toLowerCase();
        if (name.includes(q) || id.includes(q) || room.includes(q)) {
          out.push({
            type: "booking",
            label: `${b.bookingid} — ${b.guestname}`,
            sub: `${b.checkin} → ${b.checkout}  ·  ${b.status}`,
            path: `${guestBase}/bookings`,
          });
        }
      });

      // rooms
      data.rooms.forEach((r: any) => {
        const num  = String(r.roomnumber || r.roomNumber || "").toLowerCase();
        const type = (r.roomtype || r.roomType || "").toLowerCase();
        if (num.includes(q) || type.includes(q)) {
          out.push({
            type: "room",
            label: `Room ${r.roomnumber || r.roomNumber}`,
            sub: r.roomtype || r.roomType || "",
            path: `${guestBase}/rooms`,
          });
        }
      });

      setResults(out.slice(0, 8));
      setOpen(out.length > 0);
    })();
  }, [query]);

  // Auto-focus mobile search input when opened
  useEffect(() => {
    if (mobileSearchOpen) {
      setTimeout(() => mobileInputRef.current?.focus(), 50);
    } else {
      setQuery("");
      setOpen(false);
    }
  }, [mobileSearchOpen]);

  const closeMobileSearch = () => setMobileSearchOpen(false);

  const pick = (r: Result) => {
    navigate(r.path);
    setQuery("");
    setOpen(false);
    setMobileSearchOpen(false);
  };

  const icon = (type: Result["type"]) => {
    if (type === "guest")   return <User size={13} />;
    if (type === "booking") return <Calendar size={13} />;
    return <BedDouble size={13} />;
  };

  const typeLabel = (type: Result["type"]) =>
    type === "guest" ? "Guest" : type === "booking" ? "Booking" : "Room";

  const ResultsList = ({ mobile }: { mobile?: boolean }) => (
    <>
      {open && results.length > 0 && (
        <div className={mobile ? "search-dropdown mobile-search-dropdown" : "search-dropdown"}>
          {results.map((r, i) => (
            <button key={i} className="search-result-item" onClick={() => pick(r)}>
              <span className={`search-result-icon type-${r.type}`}>{icon(r.type)}</span>
              <span className="search-result-body">
                <span className="search-result-label">{r.label}</span>
                <span className="search-result-sub">{r.sub}</span>
              </span>
              <span className="search-result-type">{typeLabel(r.type)}</span>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && !loading && query.trim() && (
        <div className={mobile ? "search-dropdown mobile-search-dropdown" : "search-dropdown"}>
          <div className="search-no-results">No results for "{query}"</div>
        </div>
      )}
    </>
  );

  return (
    <>
      <header className="topbar">
        {/* HAMBURGER — mobile only */}
        <button className="topbar-hamburger" onClick={onOpenSidebar} aria-label="Open menu">
          <Menu size={22} />
        </button>

        {/* LEFT */}
        <div className="topbar-left">
          {hotelLogo && <img src={hotelLogo} alt={hotelName} className="topbar-hotel-logo" />}
          <h3>
            Welcome, <span className="manager">{managerName}</span>
            <span className="hotel-name">{hotelName}</span>
          </h3>
        </div>

        {/* MIDDLE — desktop search */}
        <div className="topbar-center">
          <div className="search-wrap" ref={boxRef}>
            <div className="search-box">
              <Search size={16} style={{ color: "rgba(242,235,217,0.45)", flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search guests, bookings, rooms…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === "Escape") { setOpen(false); setQuery(""); } }}
              />
              {loading && <span className="search-spinner" />}
            </div>
            <ResultsList />
          </div>
        </div>

        {/* RIGHT */}
        <div className="topbar-right">
          <span className="clock">{clock}</span>
          {/* Mobile search icon */}
          <button className="topbar-search-icon" onClick={() => setMobileSearchOpen(true)} aria-label="Search">
            <Search size={20} />
          </button>
          <NotificationBell />
        </div>
      </header>

      {/* Mobile search overlay */}
      {mobileSearchOpen && (
        <div className="mobile-search-overlay">
          <div className="mobile-search-bar">
            <Search size={18} className="mobile-search-icon-inner" />
            <input
              ref={mobileInputRef}
              type="text"
              className="mobile-search-input"
              placeholder="Search guests, bookings, rooms…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === "Escape") closeMobileSearch(); }}
            />
            {loading && <span className="search-spinner" />}
            <button className="mobile-search-close" onClick={closeMobileSearch}>
              <X size={20} />
            </button>
          </div>
          <ResultsList mobile />
          {!query.trim() && (
            <div className="mobile-search-hint">Type to search guests, bookings or rooms…</div>
          )}
        </div>
      )}
    </>
  );
}
